<template>
  <Card
    :id="id"
    class="location-card limited"
    :class="{
      pickup: isPickup,
      dropoff: !isPickup,
      'in-progress': location.isInProgress,
      complete: isStopResolved,
    }"
  >
    <template #content>
      <div class="top-row flex">
        <KeyValuePair
          :label="locationLabel"
          :value="`${stopTypeDisplay} ${pickDropSequenceNumber ?? ''}`"
          class="bg-gray-lighter top-row-cell address-cell bold-label"
        />

        <KeyValuePair
          class="top-row-cell"
          :label="`${stopTypeDisplay} ${pickDropSequenceNumber ?? ''}`"
          :value="`${totalWeightString}\n${numLineItemsString}`"
          uppercase-label
        >
          <template #value>
            <LabeledIcon icon="pi pi-briefcase" :label="totalWeightString" />
            <router-link
              v-if="firstLineItem?.domId"
              :to="{ hash: `#${firstLineItem?.domId}`, query: queryParams }"
              class="labeled-icon"
            >
              <i class="pi pi-list" aria-hidden="true" />
              {{ numLineItemsString }}
            </router-link>
            <LabeledIcon v-else icon="pi pi-list" :label="numLineItemsString" />
          </template>
        </KeyValuePair>

        <div class="top-row-cell primary-appt">
          <LocationCardDateRange
            v-if="primaryAppointmentRange"
            :label="primaryAppointmentRange.label"
            :start="location[primaryAppointmentRange.start.field]"
            :end="location[primaryAppointmentRange.end.field]"
            uppercase-label
          />
          <LocationCardDate
            v-else
            :label="primaryAppointment.label"
            :date="location[primaryAppointment.field]"
          />
        </div>
      </div>

      <div class="accessorials">
        <ul
          v-if="location.requestedAccessorials.length"
          class="flex flex-gap-1 flex-wrap list-none p-0 m-0"
          role="list"
        >
          <li
            v-for="(accessorial, i) in location.requestedAccessorials"
            :key="accessorial.id"
          >
            <AccessorialInlineEdit
              :model-value="location.requestedAccessorials[i]"
              :editable="false"
            />
          </li>
        </ul>
        <div v-else-if="!location.requestedAccessorials.length">
          {{
            t('common.noParam', {
              param: t('referenceData.accessorials', 2),
            })
          }}
        </div>
      </div>

      <div v-if="!hideNotes" class="bg-gray-lighter location-notes">
        <KeyValuePair
          :label="t('shippingPoints.carrierSpecialInstructions')"
          :value="location.shippingPointDetails?.carrierSpecialInstructions"
        />
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { computed, PropType, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { translateNumberedEntity } from '@rygen/rygen-ui/i18n';
import { WeightUom } from '@rygen/rygen-ui/model';

import { useLineItemsSummary, useShippingAppointments } from '@/composables';
import {
  LineItem,
  OrderShippingPoint,
  ShipmentStopType,
  ShippingPoint,
} from '@/model';

import AccessorialInlineEdit from '@/components/accessorial/AccessorialInlineEdit.vue';
import LocationCardDate from '@/components/location/LocationCardDate.vue';
import LocationCardDateRange from '@/components/location/LocationCardDateRange.vue';
import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Card from 'primevue/card';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  location: {
    type: Object as PropType<ShippingPoint>,
    default: () => new OrderShippingPoint(),
  },
  lineItems: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  pickDropSequenceNumber: {
    type: Number,
    default: () => undefined,
  },
  requireBothDates: {
    type: Boolean,
    default: () => false,
  },
  hideNotes: {
    type: Boolean,
    default: () => true,
  },
});
const {
  id,
  location,
  lineItems,
  pickDropSequenceNumber,
  requireBothDates,
  hideNotes,
} = toRefs(props);

const { t } = useI18n();
const { totalWeight } = useLineItemsSummary(lineItems);
const router = useRouter();
const { primaryAppointment, primaryAppointmentRange } =
  useShippingAppointments(location);

const queryParams = computed(() => router.currentRoute.value.query);

const isStopResolved = computed(() => {
  return location.value.isResolved(requireBothDates.value);
});

const totalWeightString = computed(() => {
  return translateNumberedEntity(
    totalWeight.value,
    `uoms.weight.abbreviations.${WeightUom.POUND}`,
  );
});

const numLineItemsString = computed(() => {
  return translateNumberedEntity(lineItems.value.length, 'lineItems.lineItems');
});

const firstLineItem: Ref<LineItem | undefined> = computed(
  () => lineItems.value[0],
);

const isPickup = computed(() => {
  return location.value.stopType === ShipmentStopType.PICKUP;
});

const stopTypeDisplay = computed(() => {
  return isPickup.value ? t('shipments.picks') : t('shipments.drops');
});

const locationLabel = computed(
  () => location.value.location.cityStatePostalCode,
);
</script>

<style>
.location-card.limited {
  --stop-type-default-color: var(--p-gray-300);
  --stop-type-gradient: linear-gradient(
      var(--stop-type-accent-color),
      var(--stop-type-default-color) 60%
    )
    1;

  border-left: 4px solid var(--stop-type-default-color);

  &.in-progress {
    border-image: var(--stop-type-gradient);
  }

  &.complete {
    border-left-color: var(--stop-type-accent-color);
  }

  > .p-card-body {
    padding: 0;
    border-bottom: var(--divider);
    padding-right: 0;

    .top-row {
      align-items: stretch;

      .top-row-cell {
        flex: 1 0 auto;
        padding: var(--sp-3) var(--sp-6);
        /* important to override KeyValuePair !important */
        height: auto !important;
        border-right: var(--divider);
        flex-grow: 1;
        max-width: 25%;

        &:first-of-type {
          max-width: 100%;
        }

        &:last-of-type {
          border-right: none;
        }

        &.address-cell {
          position: relative;
        }
      }
    }

    .accessorials {
      border-top: var(--divider);
      padding: var(--sp-6);
    }

    .location-notes {
      border-top: var(--divider);
      padding: var(--sp-3) var(--sp-6);
    }

    .key-value-pair {
      .key-value-pair-label {
        font-size: 14px;
      }

      &.bold-label {
        .key-value-pair-label {
          font-weight: 600;
        }
      }
    }
  }
}
</style>
